import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import styled from "styled-components"
import useDebounce from "../../hooks/useDebounce"
import { setEmail } from "../../store/signup"
import { setEventData } from "../../store/events"

const isArabic = navigator.language.startsWith("ar");
const textAlign = isArabic ? "right" : "left";

// eslint-disable-next-line
export const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

const Input = styled.input`
  width: 100%;
  height: 56px;
  border: none;
  border: ${(props) =>
    props.error ? `1px solid ${props.theme.colors.colorPrimary}` : `1px solid ${props.theme.colors.colorInputBorder}`};
  border-radius: 12px;
  background: ${(props) => props.theme.colors.colorWhite};
  padding: 18px 15px;
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font16};
  line-height: ${(props) => props.theme.lineHeights.lineHeight19};
  color: ${(props) => props.theme.colors.colorText};
  margin-bottom: 16px;
  outline: none;
  text-align: ${textAlign};

  &::placeholder {
    color: ${(props) => props.theme.colors.colorInputPlaceholder};
    font-weight: ${(props) => props.theme.fontWeights.extraLight};
  }

  &:focus,
  &:active {
    outline: none;
    border: ${(props) => `1px solid ${props.theme.colors.colorPrimary}`};
  }
  @media screen and (max-width: 450px) {
    margin-bottom: 8px;
  }
`

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row${isArabic ? '-reverse' : ''};
  justify-content: space-between;
  min-height: 30px;
  @media screen and (max-width: 350px) {
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`

const DomainBtn = styled.button`
  box-sizing: border-box;
  width: fit-content;
  height: 28px;
  min-width: 90px;
  border: none;
  background: ${(props) => props.theme.colors.colorWhite};
  border-radius: 8px;
  border: ${(props) => `1px solid ${props.theme.colors.colorDomainBorder}`};
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
  font-size: ${(props) => props.theme.fontSizes.font12};
  line-height: ${(props) => props.theme.lineHeights.lineHeight16};
  color: ${(props) => props.theme.colors.colorDomain};
  padding: 0 5px;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`

const SignupInput = () => {
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [selected, setSelected] = useState(null)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const debouncedEmail = useDebounce(value, 200)
  const DOMAINS = useMemo(() => ['gmail.com', 'yahoo.com', 'hotmail.com'], [])

  useEffect(() => {
    if (debouncedEmail) {
      dispatch(setEmail({ email: debouncedEmail, isValid }))
      dispatch(
        setEventData({
          id: 'user_email',
          data: debouncedEmail,
        })
      )
    }
  }, [debouncedEmail, dispatch, isValid])

  useEffect(() => {
    let isEmailValid = EMAIL_REGEX.test(value)
    setIsValid(isEmailValid)

    if (isEmailValid) {
      let currentDomain = value.split('@')[1]
      setSelected(DOMAINS.indexOf(currentDomain) !== -1 ? currentDomain : null)
    }
  }, [value, DOMAINS])

  const renderDomains = useCallback(() => {
    const onAddDomainClick = (domain) => {
      if (!value) return
      setValue((prevState) => (prevState.split('@')[0] += domain))
      setSelected(domain)
    }

    const renderDomainItem = (item,i) => (
      <DomainBtn
        key={item}
        disabled={selected}
        onClick={() => onAddDomainClick('@' + item)}
        id={'domain-button-' + (i + 1)}
      >
        @{item}
      </DomainBtn>
    )

    if (value && selected) {
      return DOMAINS.filter((i) => i === selected).map(renderDomainItem)
    }

    return DOMAINS.map(renderDomainItem)
  }, [value, selected, DOMAINS])
  return (
    <>
      <Input
        type="email"
        error={value && !isValid}
        autoComplete="email"
        value={value}
        onChange={(evt) => setValue(evt.target.value)}
        placeholder={t('email')}
        id={'sign-up-input'}
      />
      <BtnContainer>{renderDomains()}</BtnContainer>
    </>
  )
}

export default SignupInput;